import type {
  SellerOrderOverview,
  SellerOrderReport,
  SellerOrderStatus,
} from '../services/api.types';
import type { jsPDF } from 'jspdf';
import pdfLogoUrl from '../../resources/roade_logo.png?url';
import { formatUnitType } from './unitType';
import { orderDeliveryDate } from './sellerOrderDisplay';

const PDF_LOGO_URL = pdfLogoUrl;

const dateTimeFormatter = new Intl.DateTimeFormat('ro-RO', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const numberFormatter = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 3 });
const moneyFormatter = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 });

export type ExportDeliveryResult = 'saved' | 'shared' | 'downloaded' | 'cancelled';

type ExportDestination =
  | { kind: 'browser' }
  | { kind: 'picker'; handle: FileSystemFileHandle }
  | { kind: 'share' }
  | { kind: 'cancelled' };

type SaveFilePickerWindow = Window & {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: Array<{
      description: string;
      accept: Record<string, string[]>;
    }>;
  }) => Promise<FileSystemFileHandle>;
};

const twoDigits = (value: number) => String(value).padStart(2, '0');

export const createExportFilename = (
  baseName: string,
  extension: 'csv' | 'pdf',
  date = new Date(),
) => `${baseName}_${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}_${twoDigits(date.getHours())}-${twoDigits(date.getMinutes())}-${twoDigits(date.getSeconds())}.${extension}`;

const statusLabels: Record<SellerOrderStatus, string> = {
  PENDING: 'În așteptare',
  DELIVERED: 'Livrată',
  CANCELLED: 'Anulată',
};

const formatDateTime = (value: string | null) => value
  ? dateTimeFormatter.format(new Date(value))
  : '';

const csvCell = (value: unknown) => {
  const rawText = String(value ?? '');
  // Spreadsheet applications may execute cells beginning with formula operators.
  const text = /^[\s\u0000-\u001f]*[=+\-@]/.test(rawText) ? `'${rawText}` : rawText;
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const saveBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

const isMobileDevice = () => typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches ?? false);

const isCancelledFileAction = (error: unknown) => error instanceof DOMException
  ? error.name === 'AbortError'
  : typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';

const requestExportDestination = async (
  filename: string,
  mimeType: string,
  extension: 'csv' | 'pdf',
): Promise<ExportDestination> => {
  if (!isMobileDevice()) return { kind: 'browser' };

  const picker = (window as SaveFilePickerWindow).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker.call(window, {
        suggestedName: filename,
        types: [{
          description: extension === 'pdf' ? 'Document PDF' : 'Fișier CSV',
          accept: { [mimeType]: [`.${extension}`] },
        }],
      });
      return { kind: 'picker', handle };
    } catch (error) {
      if (isCancelledFileAction(error)) return { kind: 'cancelled' };
    }
  }

  return { kind: 'share' };
};

const deliverFile = async (
  blob: Blob,
  filename: string,
  mimeType: string,
  destination: ExportDestination,
): Promise<ExportDeliveryResult> => {
  if (destination.kind === 'cancelled') return 'cancelled';

  if (destination.kind === 'picker') {
    const writable = await destination.handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return 'saved';
  }

  if (destination.kind === 'share') {
    const file = new File([blob], filename, { type: mimeType });
    if (typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: filename });
        return 'shared';
      } catch (error) {
        if (isCancelledFileAction(error)) return 'cancelled';
      }
    }
  }

  saveBlob(blob, filename);
  return 'downloaded';
};

const saveCsv = async (rows: unknown[][], filename: string): Promise<ExportDeliveryResult> => {
  const mimeType = 'text/csv;charset=utf-8';
  const content = rows.map((row) => row.map(csvCell).join(';')).join('\r\n');
  const destination = await requestExportDestination(filename, 'text/csv', 'csv');
  return deliverFile(new Blob([`\uFEFF${content}`], { type: mimeType }), filename, mimeType, destination);
};

let pdfLogoPromise: Promise<string | null> | null = null;

const loadPdfLogo = () => {
  if (!pdfLogoPromise) {
    pdfLogoPromise = fetch(PDF_LOGO_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`Could not load PDF logo: ${response.status}`);
        return response.blob();
      })
      .then((blob) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      }))
      .catch(() => null)
      .then((logo) => {
        if (!logo) pdfLogoPromise = null;
        return logo;
      });
  }

  return pdfLogoPromise;
};

const pdfSafe = (value: unknown) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ș/g, 's')
  .replace(/Ș/g, 'S')
  .replace(/ț/g, 't')
  .replace(/Ț/g, 'T');

const drawPdfHeader = (document: jsPDF, logo: string | null) => {
  const pageWidth = document.internal.pageSize.getWidth();

  if (logo) {
    try {
      const image = document.getImageProperties(logo);
      const scale = Math.min(34 / image.width, 22 / image.height);
      document.addImage(logo, 'PNG', 14, 0, image.width * scale, image.height * scale);
    } catch {
      document.setFont('helvetica', 'bold');
      document.setFontSize(15);
      document.setTextColor(38, 114, 0);
      document.text('roade.ro', 14, 15);
    }
  } else {
    document.setFont('helvetica', 'bold');
    document.setFontSize(15);
    document.setTextColor(38, 114, 0);
    document.text('roade.ro', 14, 15);
  }

  document.setFont('helvetica', 'normal');
  document.setFontSize(9);
  document.setTextColor(89, 98, 85);
  document.text(
    pdfSafe(`Generat la ${dateTimeFormatter.format(new Date())}`),
    pageWidth - 14,
    14,
    { align: 'right' },
  );
  document.setDrawColor(213, 222, 209);
  document.line(14, 22, pageWidth - 14, 22);
  document.setTextColor(7, 17, 5);
};

const savePdf = async (
  title: string,
  summary: string[],
  head: string[],
  body: unknown[][],
  filename: string,
): Promise<ExportDeliveryResult> => {
  const destination = await requestExportDestination(filename, 'application/pdf', 'pdf');
  if (destination.kind === 'cancelled') return 'cancelled';
  const [{ jsPDF: PdfDocument }, { autoTable }, logo] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    loadPdfLogo(),
  ]);
  const document = new PdfDocument({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  drawPdfHeader(document, logo);
  document.setFont('helvetica', 'bold');
  document.setFontSize(16);
  document.text(pdfSafe(title), 14, 31);
  document.setFont('helvetica', 'normal');
  document.setFontSize(9);
  summary.forEach((line, index) => document.text(pdfSafe(line), 14, 38 + index * 5));
  autoTable(document, {
    startY: 42 + summary.length * 5,
    head: [head.map(pdfSafe)],
    body: body.map((row) => row.map(pdfSafe)),
    styles: { fontSize: 7, cellPadding: 1.8 },
    headStyles: { fillColor: [47, 107, 45] },
    alternateRowStyles: { fillColor: [247, 249, 246] },
    margin: { top: 27, right: 14, bottom: 14, left: 14 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) drawPdfHeader(document, logo);
    },
  });
  if (destination.kind === 'browser') {
    document.save(filename);
    return 'downloaded';
  }
  return deliverFile(document.output('blob'), filename, 'application/pdf', destination);
};

const orderLocationLabel = (location: SellerOrderOverview['locations'][number]) =>
  `${location.deliveryPointName} · ${location.locationName}, ${location.countyName}${location.deliveryAddressDetails ? ` · ${location.deliveryAddressDetails}` : ''}`;

const orderRows = (overview: SellerOrderOverview) => overview.locations.flatMap((location) =>
  location.products.flatMap((product) => product.orders.map((order) => [
    orderLocationLabel(location),
    product.productTitle,
    order.customerName,
    order.customerPhone || '',
    numberFormatter.format(order.quantity),
    formatUnitType(product.unitType),
    moneyFormatter.format(order.unitPrice),
    moneyFormatter.format(order.totalPrice),
    statusLabels[order.status],
    formatDateTime(order.orderDate),
    formatDateTime(orderDeliveryDate(order)),
    order.remarks || '',
  ])));

const orderHead = [
  'Locație', 'Anunț', 'Client', 'Telefon', 'Cantitate', 'Unitate', 'Preț unitar (lei)',
  'Total (lei)', 'Stare', 'Data comenzii', 'Data livrării', 'Observații',
];

const orderSummary = (overview: SellerOrderOverview) => [
  `Comenzi: ${overview.summary.orderCount}`,
  `De încasat: ${moneyFormatter.format(overview.summary.expectedAmount)} lei`,
  `Valoare livrată: ${moneyFormatter.format(overview.summary.deliveredAmount)} lei`,
  `Valoare anulată: ${moneyFormatter.format(overview.summary.cancelledAmount)} lei`,
];

const orderDetailHead = [
  'Client', 'Telefon', 'Cantitate', 'Unitate', 'Preț unitar (lei)', 'Total (lei)',
  'Data comenzii', 'Data livrării', 'Observații', 'Stare',
];

const pdfStatusLabel = (status: SellerOrderStatus) => status === 'PENDING' ? '' : statusLabels[status];

const orderDetailRows = (
  orders: SellerOrderOverview['locations'][number]['products'][number]['orders'],
) => orders.map((order) => [
  order.customerName,
  order.customerPhone || '',
  numberFormatter.format(order.quantity),
  formatUnitType(order.unitType),
  moneyFormatter.format(order.unitPrice),
  moneyFormatter.format(order.totalPrice),
  formatDateTime(order.orderDate),
  formatDateTime(orderDeliveryDate(order)),
  order.remarks || '',
  pdfStatusLabel(order.status),
]);

export const buildOrdersPdfGroups = (overview: SellerOrderOverview) =>
  overview.locations.map((location) => ({
    location: orderLocationLabel(location),
    orderCount: location.orderCount,
    expectedAmount: location.expectedAmount,
    products: location.products.map((product) => ({
      title: product.productTitle,
      quantity: product.activeQuantity,
      unit: formatUnitType(product.unitType),
      expectedAmount: product.expectedAmount,
      rows: orderDetailRows(product.orders),
    })),
  }));

const saveOrdersPdf = async (overview: SellerOrderOverview): Promise<ExportDeliveryResult> => {
  const filename = createExportFilename('comenzi', 'pdf');
  const destination = await requestExportDestination(filename, 'application/pdf', 'pdf');
  if (destination.kind === 'cancelled') return 'cancelled';
  const [{ jsPDF: PdfDocument }, { autoTable }, logo] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    loadPdfLogo(),
  ]);
  const document = new PdfDocument({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const groups = buildOrdersPdfGroups(overview);
  const tableDocument = document as jsPDF & { lastAutoTable?: { finalY: number } };

  const addPage = () => {
    document.addPage();
    drawPdfHeader(document, logo);
    return 29;
  };

  const drawLocation = (group: (typeof groups)[number], startY: number) => {
    document.setFillColor(240, 248, 236);
    document.roundedRect(14, startY, pageWidth - 28, 10, 1.5, 1.5, 'F');
    document.setFont('helvetica', 'bold');
    document.setFontSize(10);
    document.setTextColor(38, 114, 0);
    document.text(pdfSafe(`Locație: ${group.location}`), 18, startY + 6.5);
    document.setFont('helvetica', 'normal');
    document.setFontSize(8.5);
    document.text(
      pdfSafe(`${group.orderCount} ${group.orderCount === 1 ? 'comandă' : 'comenzi'} | Total: ${moneyFormatter.format(group.expectedAmount)} lei`),
      pageWidth - 18,
      startY + 6.5,
      { align: 'right' },
    );
    document.setTextColor(7, 17, 5);
    return startY + 15;
  };

  const drawProduct = (
    product: (typeof groups)[number]['products'][number],
    startY: number,
  ) => {
    document.setFont('helvetica', 'bold');
    document.setFontSize(9.5);
    document.setTextColor(7, 17, 5);
    document.text(pdfSafe(`Anunț: ${product.title}`), 18, startY + 5);
    document.setFont('helvetica', 'normal');
    document.setFontSize(8.5);
    document.setTextColor(89, 98, 85);
    document.text(
      pdfSafe(
        `${numberFormatter.format(product.quantity)} ${product.unit} | Total: ${moneyFormatter.format(product.expectedAmount)} lei`,
      ),
      pageWidth - 18,
      startY + 5,
      { align: 'right' },
    );
    document.setTextColor(7, 17, 5);
    return startY + 9;
  };

  drawPdfHeader(document, logo);
  document.setFont('helvetica', 'bold');
  document.setFontSize(16);
  document.text('Comenzi', 14, 31);
  document.setFont('helvetica', 'normal');
  document.setFontSize(9);
  const summary = orderSummary(overview);
  summary.forEach((line, index) => {
    document.text(pdfSafe(line), 14, 38 + index * 5);
  });

  let currentY = 43 + summary.length * 5;

  groups.forEach((group) => {
    if (currentY > pageHeight - 42) currentY = addPage();
    currentY = drawLocation(group, currentY);

    group.products.forEach((product) => {
      if (currentY > pageHeight - 34) {
        currentY = addPage();
        currentY = drawLocation(group, currentY);
      }
      currentY = drawProduct(product, currentY);

      autoTable(document, {
        startY: currentY,
        head: [orderDetailHead.map(pdfSafe)],
        body: product.rows.map((row) => row.map(pdfSafe)),
        styles: { fontSize: 7, cellPadding: 1.8, overflow: 'linebreak' },
        bodyStyles: { fontSize: 8 },
        headStyles: { fillColor: [47, 107, 45] },
        alternateRowStyles: { fillColor: [247, 249, 246] },
        margin: { top: 38, right: 14, bottom: 14, left: 14 },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            drawPdfHeader(document, logo);
            document.setFont('helvetica', 'bold');
            document.setFontSize(8.5);
            document.setTextColor(38, 114, 0);
            document.text(pdfSafe(`${group.location} / ${product.title}`), 14, 30);
            document.setTextColor(7, 17, 5);
          }
        },
      });

      currentY = (tableDocument.lastAutoTable?.finalY ?? currentY) + 7;
    });

    currentY += 2;
  });

  if (destination.kind === 'browser') {
    document.save(filename);
    return 'downloaded';
  }
  return deliverFile(document.output('blob'), filename, 'application/pdf', destination);
};

export const exportOrdersCsv = (overview: SellerOrderOverview) => saveCsv(
  [orderHead, ...orderRows(overview)],
  createExportFilename('comenzi', 'csv'),
);

export const exportOrdersPdf = (overview: SellerOrderOverview) => saveOrdersPdf(overview);

const reportHead = [
  'Perioadă', 'Locație', 'Anunț', 'Comenzi', 'În așteptare', 'Livrate', 'Anulate',
  'Cantitate', 'Unitate', 'De încasat (lei)', 'Valoare livrată (lei)', 'Valoare anulată (lei)',
];

const reportRows = (report: SellerOrderReport) => report.rows.map((row) => [
  row.periodLabel,
  row.locationName || '',
  row.productTitle || '',
  row.orderCount,
  row.pendingCount,
  row.deliveredCount,
  row.cancelledCount,
  row.unitType ? numberFormatter.format(row.activeQuantity) : '',
  row.unitType ? formatUnitType(row.unitType) : '',
  moneyFormatter.format(row.expectedAmount),
  moneyFormatter.format(row.deliveredAmount),
  moneyFormatter.format(row.cancelledAmount),
]);

const reportSummary = (report: SellerOrderReport) => [
  `Interval: ${formatDateTime(report.from)} - ${formatDateTime(report.to)}`,
  `Comenzi: ${report.summary.orderCount}`,
  `De încasat: ${moneyFormatter.format(report.summary.expectedAmount)} lei`,
  `Valoare livrată: ${moneyFormatter.format(report.summary.deliveredAmount)} lei`,
  `Valoare anulată: ${moneyFormatter.format(report.summary.cancelledAmount)} lei`,
];

export const exportReportCsv = (report: SellerOrderReport) => saveCsv(
  [reportHead, ...reportRows(report)],
  createExportFilename('raport-comenzi', 'csv'),
);

export const exportReportPdf = (report: SellerOrderReport) => savePdf(
  'Raport comenzi',
  reportSummary(report),
  reportHead,
  reportRows(report),
  createExportFilename('raport-comenzi', 'pdf'),
);
